import PropTypes from "prop-types";
import { memo } from "react";

// Common Sections
import CallActivityPerformanceComponent from "@PayPlans/components/Commons/CallActivityPerformance";
import CustomerSatisfactionComponent from "@PayPlans/components/Commons/CustomerSatisfaction";
import LongevityComponent from "@PayPlans/components/Commons/Longevity";
import TopPerformerBonus from "@PayPlans/components/Commons/TopPerformer";
import VariableOperationsGpsBonusComponent from "@PayPlans/components/Commons/VariableOperationsGpsBonus";

// Sections
import CashAndLeaseBumpComponent from "@/PayPlans/components/Defaults/Managers/Finance/CashAndLeaseBump";
import CITBonusComponent from "@/PayPlans/components/Defaults/Managers/Finance/CITBonus";
import DepartmentOverrideNetIncomeComponent from "@/PayPlans/components/Defaults/Managers/Finance/DepartmentOverrideNetIncome";
import DefaultCommissionsComponent from "@/PayPlans/components/Defaults/Managers/Finance/DefaultCommissions";
import MaxPayoutComponent from "@/PayPlans/components/Defaults/Managers/Finance/MaxPayout";
import PerformanceNormalizationComponent from "@/PayPlans/components/Defaults/Managers/Finance/PerformanceNormalization";
import ProductBonusesComponent from "@/PayPlans/components/Defaults/Managers/Finance/ProductBonuses";
import PVRQualifierComponent from "@/PayPlans/components/Defaults/Managers/Finance/PVRQualifier";

function CallActivityPerformanceSection(props) {
	const { sectionId = "callActivityPerformance", performance = null } = props;
	return <CallActivityPerformanceComponent {...props} sectionId={sectionId} performance={performance} />;
}
CallActivityPerformanceSection.propTypes = {
	sectionId: PropTypes.string,
	performance: PropTypes.object,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

function CashAndLeaseBumpSection(props) {
	const {
		sectionId = "cashAndLeaseBump",
		content = {
			metric: "PVR",
			bonus: false,
			tiers: [],
		},
	} = props;
	return <CashAndLeaseBumpComponent {...props} sectionId={sectionId} content={content} />;
}
CashAndLeaseBumpSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.object,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

function CitBonusSection(props) {
	const { sectionId = "cit", content = null, citTypes = ["Dollars", "Contracts"] } = props;
	return <CITBonusComponent {...props} sectionId={sectionId} content={content} citTypes={citTypes} />;
}
CitBonusSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.object,
	citTypes: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

function CustomerSatisfactionSection(props) {
	const {
		sectionId = "customerSatisfaction",
		content = {
			makeMetrics: [],
			weighting: {
				weightingType: "UNITS",
				customWeights: [],
			},
		},
		excludesMakes = ["Cadillac"],
		errors = [],
		isService = false,
		useOnlyIndividualMetrics = false,
		useOnlyStoreMetrics = true,
	} = props;

	return (
		<CustomerSatisfactionComponent
			{...props}
			sectionId={sectionId}
			content={content}
			excludesMakes={excludesMakes}
			errors={errors}
			isService={isService}
			useOnlyIndividualMetrics={useOnlyIndividualMetrics}
			useOnlyStoreMetrics={useOnlyStoreMetrics}
		/>
	);
}
CustomerSatisfactionSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		makeMetrics: PropTypes.array,
		weighting: PropTypes.shape({
			weightingType: PropTypes.string,
			customWeights: PropTypes.array,
		}),
	}),
	excludesMakes: PropTypes.array,
	errors: PropTypes.array,
	isService: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useOnlyIndividualMetrics: PropTypes.bool,
	useOnlyStoreMetrics: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};

function DepartmentOverrideNetIncomeSection(props) {
	const { sectionId = "departmentOverrideNetIncome", content = null } = props;
	return <DepartmentOverrideNetIncomeComponent sectionId={sectionId} content={content} {...props} />;
}
DepartmentOverrideNetIncomeSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.object,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

function FMDefaultTabsSection(props) {
	const { sectionId = "default", globalGrossAndUnits = false, isGuaranteedDraw = false, errors = [], formatVersion = null } = props;
	return (
		<DefaultCommissionsComponent
			{...props}
			sectionId={sectionId}
			backProducts={formatVersion?.backProducts || []}
			tabsProps={{
				tabs: ["New Vehicles", "Pre-Owned Vehicles"],
				errors: errors || [],
				globalGrossAndUnits,
				isGuaranteedDraw,
			}}
		/>
	);
}
FMDefaultTabsSection.propTypes = {
	sectionId: PropTypes.string,
	commissions: PropTypes.shape({
		new: PropTypes.object,
		preOwned: PropTypes.object,
	}),
	globalGrossAndUnits: PropTypes.bool,
	isGuaranteedDraw: PropTypes.bool,
	errors: PropTypes.array,
	formatVersion: PropTypes.shape({
		backProducts: PropTypes.array,
	}),
	loading: PropTypes.bool,
	loadingProducts: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

function LongevitySection(props) {
	const { sectionId = "longevity", content = [] } = props;
	return <LongevityComponent {...props} sectionId={sectionId} content={content} />;
}
LongevitySection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

function MaxPayoutSection(props) {
	return <MaxPayoutComponent {...props} />;
}
MaxPayoutSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		frontend: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
		backend: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
		afterChargebacks: PropTypes.bool,
	}),
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

function PerformanceNormalizationSection(props) {
	return <PerformanceNormalizationComponent {...props} />;
}
PerformanceNormalizationSection.propTypes = {
	sectionId: PropTypes.string,
	normalization: PropTypes.string,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};

function ProductBonusesSection(props) {
	const { sectionId = "productBonuses", products = [], formatVersion = null } = props;
	return (
		<ProductBonusesComponent {...props} sectionId={sectionId} products={products} backProducts={formatVersion?.backProducts || []} />
	);
}
ProductBonusesSection.propTypes = {
	sectionId: PropTypes.string,
	products: PropTypes.array,
	formatVersion: PropTypes.shape({
		backProducts: PropTypes.array,
	}),
	loading: PropTypes.bool,
	loadingProducts: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

function PVRQualifierSection(props) {
	return <PVRQualifierComponent {...props} />;
}
PVRQualifierSection.propTypes = {
	sectionId: PropTypes.string,
	qualifiers: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

function TopPerformanceBonusSection(props) {
	const { sectionId = "topSalesBonus", content = [] } = props;
	return <TopPerformerBonus {...props} sectionId={sectionId} content={content} />;
}
TopPerformanceBonusSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};

const VariableOperationsGpsBonusSection = (props) => {
	return <VariableOperationsGpsBonusComponent {...props} />;
}

VariableOperationsGpsBonusSection.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.object,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};

const CallActivityPerformance = memo(CallActivityPerformanceSection);
const CashAndLeaseBump = memo(CashAndLeaseBumpSection);
const CitBonus = memo(CitBonusSection);
const CustomerSatisfaction = memo(CustomerSatisfactionSection);
const DefaultCommissions = memo(FMDefaultTabsSection);
const DepartmentOverrideNetIncome = memo(DepartmentOverrideNetIncomeSection);
const Longevity = memo(LongevitySection);
const MaxPayout = memo(MaxPayoutSection);
const ProductBonuses = memo(ProductBonusesSection);
const PVRQualifier = memo(PVRQualifierSection);
const PerformanceNormalization = memo(PerformanceNormalizationSection);
const TopPerformance = memo(TopPerformanceBonusSection);
const VariableOperationsGpsBonus = memo(VariableOperationsGpsBonusSection);

export {
	CallActivityPerformance,
	CashAndLeaseBump,
	CitBonus,
	CustomerSatisfaction,
	DefaultCommissions,
	DepartmentOverrideNetIncome,
	Longevity,
	MaxPayout,
	PerformanceNormalization,
	ProductBonuses,
	PVRQualifier,
	TopPerformance,
	VariableOperationsGpsBonus,
};
